import { useState, useEffect, useCallback } from 'react';
import { Loader2, Check, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { getWarehouseReturns, confirmWarehouseReturn, cancelWarehouseReturn, syncWarehouseReturns } from '@/lib/api';

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const PILL = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
};
const STATUS_RU = { pending: '⏳ Ожидает', received: '✅ Принято', cancelled: '✖ Отклонено' };
export const RETURN_CONDITIONS = [
  ['good', '✅ Годный'], ['mistake', '📝 Ошибка'], ['expired', '⏰ Срок истёк'], ['damaged', '💔 Брак'],
];
const COND_LABEL = Object.fromEntries(RETURN_CONDITIONS);

export function fmtDT(iso) {
  const t = new Date(iso).getTime();
  if (!t) return '';
  return new Date(t).toLocaleString('ru', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ago(iso) {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return 'только что';
  if (m < 60) return `${m} мин назад`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч назад`;
  return new Date(t).toLocaleDateString('ru', { day: 'numeric', month: 'short' });
}

export const ReturnsSection = ({ warehouses, onPendingChange }) => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [whFor, setWhFor] = useState({});
  const [condFor, setCondFor] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getWarehouseReturns().then(({ data }) => {
      setReturns(data || []);
      onPendingChange?.((data || []).filter(r => r.status === 'pending').length);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [onPendingChange]);
  useEffect(() => {
    syncWarehouseReturns()
      .then(({ data }) => { if (data?.created > 0) toast.success(`↩️ Из таблицы добавлено возвратов: ${data.created}`); })
      .catch(() => {})
      .finally(load);
  }, [load]);

  const confirm = async (r) => {
    const wid = whFor[r.return_id] || warehouses[0]?.warehouse_id;
    if (!wid) { toast.error('Выберите склад'); return; }
    setBusy(r.return_id);
    try {
      await confirmWarehouseReturn(r.return_id, wid, condFor[r.return_id] || 'good');
      toast.success('Возврат принят · склад обновлён · MR уведомлён');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ошибка'); }
    finally { setBusy(null); }
  };

  const reject = async (r) => {
    const note = window.prompt('Причина отклонения (необязательно):', '');
    if (note === null) return;
    setBusy(r.return_id);
    try {
      await cancelWarehouseReturn(r.return_id, note.trim());
      toast.success('Возврат отклонён · MR уведомлён');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ошибка'); }
    finally { setBusy(null); }
  };

  const pending = returns.filter(r => r.status === 'pending');

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="returns-section">
      <div className="border bg-card p-3 flex items-center gap-2" style={{ borderRadius: 14 }}>
        <RotateCcw className="w-4 h-4 text-muted-foreground" />
        <span className="text-[12px] font-semibold">Возвраты на склад</span>
        {pending.length > 0 && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="pending-returns-badge">
            {pending.length} ожидают
          </span>
        )}
      </div>

      {returns.length === 0 && (
        <p className="text-center text-[12px] text-muted-foreground py-8" data-testid="no-returns">Возвратов пока нет</p>
      )}

      {returns.map(r => (
        <div key={r.return_id} className="border bg-card p-3.5 space-y-2" style={{ borderRadius: 14 }} data-testid={`return-card-${r.return_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9.5px] font-bold px-2 py-0.5 rounded-full border ${PILL[r.status] || ''}`} data-testid={`return-status-${r.return_id}`}>
              {STATUS_RU[r.status] || r.status}
            </span>
            <span className="text-[9.5px] px-2 py-0.5 rounded-full border bg-muted/50 text-muted-foreground">
              {r.source === 'telegram' ? '✈️ Telegram' : r.source === 'ct_grid' ? '📋 Таблица' : '💻 Web'}
            </span>
            <span className="ml-auto text-[10px] text-muted-foreground tabular-nums" data-testid={`return-created-${r.return_id}`}>📅 {fmtDT(r.created_at)} · {ago(r.created_at)}</span>
          </div>
          <p className="text-[13px] font-bold">{r.apteka_name}</p>
          <p className="text-[12px]" style={{ fontFamily: MONO }}>
            {r.product_name} × <b>{r.qty}</b> шт
          </p>
          <p className="text-[10.5px] text-muted-foreground">
            📝 {r.reason} · 👤 {r.requested_by_name}
            {r.condition && r.status === 'received' ? ` · ${COND_LABEL[r.condition] || r.condition}` : ''}
            {r.status === 'received' && r.received_at ? ` · ✓ ${fmtDT(r.received_at)}` : ''}
            {r.cancel_note ? ` · Отклонено: ${r.cancel_note}` : ''}
          </p>
          {r.status === 'pending' && (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <select
                value={whFor[r.return_id] || warehouses[0]?.warehouse_id || ''}
                onChange={e => setWhFor(p => ({ ...p, [r.return_id]: e.target.value }))}
                className="h-9 px-2 rounded-lg border bg-card text-[11px] flex-1 min-w-[120px]"
                data-testid={`return-warehouse-select-${r.return_id}`}>
                {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
              </select>
              <select
                value={condFor[r.return_id] || 'good'}
                onChange={e => setCondFor(p => ({ ...p, [r.return_id]: e.target.value }))}
                className="h-9 px-2 rounded-lg border bg-card text-[11px] min-w-[110px]"
                data-testid={`return-condition-select-${r.return_id}`}>
                {RETURN_CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button onClick={() => confirm(r)} disabled={busy === r.return_id}
                className="h-9 px-3 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
                style={{ background: '#1f9d6c' }} data-testid={`confirm-return-btn-${r.return_id}`}>
                {busy === r.return_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Принято
              </button>
              <button onClick={() => reject(r)} disabled={busy === r.return_id}
                className="h-9 px-3 rounded-lg text-[11px] font-bold border text-rose-600 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`reject-return-btn-${r.return_id}`}>
                <X className="w-3.5 h-3.5" /> Отклонить
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
